import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Link2, Copy, Share2, Users, DollarSign, 
  CheckCircle, Clock, ExternalLink, Gift,
  TrendingUp, Percent
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '/api';

export default function ReferralDashboard() {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newCode, setNewCode] = useState('');
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/instructor/referral`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralData(response.data);
      setNewCode(response.data.referral_code);
    } catch (error) {
      console.error('Error fetching referral data:', error);
      toast.error(isEnglish ? 'Failed to load referral data' : 'Referans verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(isEnglish ? 'Copied to clipboard!' : 'Panoya kopyalandı!');
  };

  const updateReferralCode = async () => {
    if (!newCode || newCode.length < 3) {
      toast.error(isEnglish ? 'Code must be at least 3 characters' : 'Kod en az 3 karakter olmalı');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/instructor/referral/update-code?new_code=${encodeURIComponent(newCode)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setReferralData(prev => ({
        ...prev,
        referral_code: response.data.referral_code,
        referral_link: response.data.referral_link
      }));
      setEditing(false);
      toast.success(isEnglish ? 'Referral code updated!' : 'Referans kodu güncellendi!');
    } catch (error) {
      console.error('Error updating code:', error);
      toast.error(error.response?.data?.detail || (isEnglish ? 'Failed to update code' : 'Kod güncellenemedi'));
    }
  };

  const shareLink = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Pilatika',
        text: isEnglish ? 'Join me on Pilatika!' : 'Pilatika\'da bana katılın!',
        url: referralData?.referral_link
      });
    } else {
      copyToClipboard(referralData?.referral_link);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(isEnglish ? 'en-US' : 'tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(isEnglish ? 'en-US' : 'tr-TR');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-8 px-4 md:px-8 bg-gray-50">
      <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-8 text-white">
        <div className="flex items-center gap-3 mb-4">
          <Gift className="h-8 w-8" />
          <h1 className="text-2xl font-bold">
            {isEnglish ? 'Referral Program' : 'Referans Programı'}
          </h1>
        </div>
        <p className="text-purple-100 mb-6">
          {isEnglish 
            ? 'Share your link and earn 85% commission on referred sales!'
            : 'Linkinizi paylaşın, referans satışlarından %85 komisyon kazanın!'}
        </p>
        
        {/* Referral Link Box */}
        <div className="bg-white/10 backdrop-blur rounded-xl p-4">
          <label className="text-sm text-purple-100 mb-2 block">
            {isEnglish ? 'Your Referral Link' : 'Referans Linkiniz'}
          </label>
          <div className="flex gap-2">
            <Input
              value={referralData?.referral_link || ''}
              readOnly
              className="bg-white/20 border-white/30 text-white placeholder:text-white/50"
            />
            <Button 
              variant="secondary" 
              onClick={() => copyToClipboard(referralData?.referral_link)}
            >
              <Copy className="h-4 w-4" />
            </Button>
            <Button variant="secondary" onClick={shareLink}>
              <Share2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-green-50 to-green-100">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">{isEnglish ? 'Referral Rate' : 'Referans Oranı'}</p>
                <p className="text-3xl font-bold text-green-700">%{referralData?.commission_rate}</p>
              </div>
              <div className="p-3 bg-green-200 rounded-lg">
                <Percent className="h-6 w-6 text-green-700" />
              </div>
            </div>
            <p className="text-xs text-green-600 mt-2">
              {isEnglish ? 'vs 80% normal rate' : 'Normal oran: %80'}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{isEnglish ? 'Total Referrals' : 'Toplam Referans'}</p>
                <p className="text-2xl font-bold">{referralData?.stats?.total_referrals || 0}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{isEnglish ? 'Completed' : 'Tamamlanan'}</p>
                <p className="text-2xl font-bold text-green-600">{referralData?.stats?.completed_referrals || 0}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{isEnglish ? 'Earnings' : 'Kazanç'}</p>
                <p className="text-2xl font-bold text-purple-600">
                  {formatCurrency(referralData?.stats?.total_earnings)}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Customize Code */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              {isEnglish ? 'Customize Your Code' : 'Kodunuzu Özelleştirin'}
            </CardTitle>
            <CardDescription>
              {isEnglish 
                ? 'Make your referral link memorable'
                : 'Referans linkinizi akılda kalıcı yapın'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block">
                  {isEnglish ? 'Referral Code' : 'Referans Kodu'}
                </label>
                <div className="flex gap-2">
                  <Input
                    value={newCode}
                    onChange={(e) => setNewCode(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
                    placeholder="yourcode"
                    disabled={!editing}
                    maxLength={20}
                  />
                  {editing ? (
                    <>
                      <Button onClick={updateReferralCode}>
                        {isEnglish ? 'Save' : 'Kaydet'}
                      </Button>
                      <Button variant="outline" onClick={() => {
                        setEditing(false);
                        setNewCode(referralData?.referral_code);
                      }}>
                        {isEnglish ? 'Cancel' : 'İptal'}
                      </Button>
                    </>
                  ) : (
                    <Button variant="outline" onClick={() => setEditing(true)}>
                      {isEnglish ? 'Edit' : 'Düzenle'}
                    </Button>
                  )}
                </div>
              </div>
              
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  {isEnglish ? 'Preview:' : 'Önizleme:'}
                </p>
                <p className="font-mono text-purple-600 break-all">
                  pilatika.com/ref/{newCode}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* How It Works */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              {isEnglish ? 'How It Works' : 'Nasıl Çalışır?'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold">1</div>
                <div>
                  <p className="font-medium">{isEnglish ? 'Share Your Link' : 'Linkinizi Paylaşın'}</p>
                  <p className="text-sm text-gray-500">
                    {isEnglish 
                      ? 'Share your unique referral link on social media, email, or anywhere!'
                      : 'Sosyal medya, e-posta veya herhangi bir yerde paylaşın!'}
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold">2</div>
                <div>
                  <p className="font-medium">{isEnglish ? 'They Sign Up' : 'Kayıt Olsunlar'}</p>
                  <p className="text-sm text-gray-500">
                    {isEnglish 
                      ? 'When someone registers through your link, they\'re connected to you.'
                      : 'Linkinizden kayıt olduklarında size bağlanırlar.'}
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold">3</div>
                <div>
                  <p className="font-medium">{isEnglish ? 'Earn 85%' : '%85 Kazanın'}</p>
                  <p className="text-sm text-gray-500">
                    {isEnglish 
                      ? 'When they purchase your courses, you earn 85% instead of 80%!'
                      : 'Kurslarınızı satın aldıklarında %80 yerine %85 kazanırsınız!'}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Referred Users */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {isEnglish ? 'Referred Users' : 'Referans Kullanıcılar'}
          </CardTitle>
          <CardDescription>
            {referralData?.referred_users?.length || 0} {isEnglish ? 'users joined via your link' : 'kullanıcı linkinizle katıldı'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {referralData?.referred_users?.length > 0 ? (
            <div className="space-y-3">
              {referralData.referred_users.map((user, index) => (
                <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <Users className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium">{user.full_name}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge className={user.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                      {user.status === 'completed' ? (
                        <><CheckCircle className="h-3 w-3 mr-1" /> {isEnglish ? 'Purchased' : 'Satın Aldı'}</>
                      ) : (
                        <><Clock className="h-3 w-3 mr-1" /> {isEnglish ? 'Pending' : 'Beklemede'}</>
                      )}
                    </Badge>
                    {user.commission_earned > 0 && (
                      <p className="text-sm text-green-600 mt-1">
                        +{formatCurrency(user.commission_earned)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>{isEnglish ? 'No referrals yet' : 'Henüz referans yok'}</p>
              <p className="text-sm mt-1">
                {isEnglish ? 'Share your link to start earning!' : 'Kazanmaya başlamak için linkinizi paylaşın!'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
